import React, { useState, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import NotificationBell from './NotificationBell';
import EmailVerificationBanner from './EmailVerificationBanner';
import TimezoneDisplay from './TimezoneDisplay';
import CommandPalette from './CommandPalette';
import {
  LayoutDashboard, CheckSquare, Users, FileText, Clock,
  TrendingUp, UserPlus, Settings, LogOut, Menu, X, Calendar,
  MessageSquare, CreditCard, Award, Shield, Briefcase, Activity,
  FolderOpen, UserCog, LogIn, DollarSign, ClipboardList,
  GitCompare, ListChecks, BarChart3, ChevronDown, PanelLeftClose, PanelLeft, Mail, Search, Bell
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SidebarGroup = ({ title, items, location, collapsed, onNavClick }) => {
  const [open, setOpen] = useState(true);
  const hasActive = items.some(i => location.pathname === i.path);

  if (collapsed) {
    return (
      <div className="space-y-0.5 py-1">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              title={item.label}
              className={`flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-all duration-200 ${
                isActive
                  ? 'bg-[#E5F7E7] text-[#5FD068] border-l-2 border-[#5FD068]'
                  : 'text-[#4B5563] hover:bg-[#F3F4F6]'
              }`}
              onClick={onNavClick}
            >
              <Icon className="w-[18px] h-[18px]" />
            </Link>
          );
        })}
      </div>
    );
  }

  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-4 py-2 text-xs uppercase tracking-[0.15em] font-medium text-[#9CA3AF] hover:text-[#4B5563] transition-colors"
      >
        <span>{title}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? '' : '-rotate-90'}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-0.5 px-2">
              {items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                      isActive
                        ? 'bg-[#E5F7E7] text-[#0A0D0A] font-medium border-l-[3px] border-[#5FD068]'
                        : 'text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A0D0A] border-l-[3px] border-transparent'
                    }`}
                    onClick={onNavClick}
                  >
                    <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const sidebarRef = useRef(null);
  const scrollPosRef = useRef(0);
  const isNavigatingRef = useRef(false);

  const handleSidebarScroll = useCallback(() => {
    if (sidebarRef.current && !isNavigatingRef.current) {
      scrollPosRef.current = sidebarRef.current.scrollTop;
    }
  }, []);

  const handleNavClick = useCallback(() => {
    isNavigatingRef.current = true;
    setIsMobileMenuOpen(false);
  }, []);

  useLayoutEffect(() => {
    const saved = scrollPosRef.current;
    if (sidebarRef.current && saved > 0) {
      sidebarRef.current.scrollTop = saved;
    }
    requestAnimationFrame(() => { isNavigatingRef.current = false; });
  }, [location.pathname]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user.role || 'client';

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const sidebarGroups = useMemo(() => {
    if (role === 'admin') {
      return [
        {
          title: 'Overview',
          items: [
            { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
            { icon: TrendingUp, label: 'Analytics', path: '/admin/advanced-analytics' },
            { icon: BarChart3, label: 'Advanced Reporting', path: '/admin/advanced-reporting' },
          ]
        },
        {
          title: 'Team',
          items: [
            { icon: ClipboardList, label: 'VA Management', path: '/admin/va-management' },
            { icon: UserPlus, label: 'VA Assignments', path: '/admin/va-assignments' },
            { icon: Clock, label: 'VA Scheduling', path: '/admin/va-scheduling' },
            { icon: Calendar, label: 'VA Availability', path: '/admin/va-availability' },
            { icon: Activity, label: 'VA Heatmap', path: '/admin/va-heatmap' },
            { icon: FileText, label: 'VA Utilization', path: '/admin/va-utilization' },
            { icon: GitCompare, label: 'VA Comparison', path: '/admin/va-comparison' },
            { icon: Users, label: 'Team Directory', path: '/team' },
            { icon: UserCog, label: 'User Management', path: '/admin/users' },
          ]
        },
        {
          title: 'Operations',
          items: [
            { icon: CheckSquare, label: 'All Tasks', path: '/tasks' },
            { icon: ListChecks, label: 'Bulk Assign', path: '/admin/bulk-assign' },
            { icon: Activity, label: 'All Reports', path: '/reports' },
            { icon: Briefcase, label: 'All Timesheets', path: '/timesheets' },
            { icon: Calendar, label: 'Leave Requests', path: '/leave' },
          ]
        },
        {
          title: 'Communication',
          items: [
            { icon: MessageSquare, label: 'Chat', path: '/chat' },
            { icon: FileText, label: 'Weekly Digest', path: '/admin/weekly-digest' },
            { icon: Mail, label: 'Email Templates', path: '/admin/email-templates' },
          ]
        },
        {
          title: 'Finance',
          items: [
            { icon: DollarSign, label: 'Customers', path: '/admin/customers' },
            { icon: CreditCard, label: 'Billing', path: '/billing' },
          ]
        }
      ];
    }

    if (role === 'va') {
      return [
        {
          title: 'Overview',
          items: [
            { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
            { icon: TrendingUp, label: 'Performance', path: '/performance' },
          ]
        },
        {
          title: 'Work',
          items: [
            { icon: CheckSquare, label: 'My Tasks', path: '/tasks' },
            { icon: Users, label: 'My Clients', path: '/team' },
            { icon: LogIn, label: 'Attendance', path: '/attendance' },
            { icon: Clock, label: 'Timesheets', path: '/timesheets' },
            { icon: FileText, label: 'Submit EOD', path: '/va-reports' },
          ]
        },
        {
          title: 'Profile',
          items: [
            { icon: Calendar, label: 'Leave Requests', path: '/leave' },
            { icon: Award, label: 'Skills Assessment', path: '/assessments' },
            { icon: FolderOpen, label: 'My Portfolio', path: '/portfolio' },
            { icon: UserCog, label: 'My Profile', path: '/profile' },
          ]
        },
        {
          title: 'Communication',
          items: [
            { icon: MessageSquare, label: 'Chat', path: '/chat' },
          ]
        }
      ];
    }

    // Client
    return [
      {
        title: 'Overview',
        items: [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
          { icon: TrendingUp, label: 'Performance', path: '/performance' },
        ]
      },
      {
        title: 'Work',
        items: [
          { icon: CheckSquare, label: 'Tasks', path: '/tasks' },
          { icon: Users, label: 'My VAs', path: '/team' },
          { icon: FileText, label: 'EOD Reports', path: '/reports' },
          { icon: Clock, label: 'Timesheets', path: '/timesheets' },
        ]
      },
      {
        title: 'Account',
        items: [
          { icon: UserPlus, label: 'Hire VA', path: '/hire' },
          { icon: CreditCard, label: 'Billing', path: '/billing' },
          { icon: UserCog, label: 'My Profile', path: '/profile' },
        ]
      },
      {
        title: 'Communication',
        items: [
          { icon: MessageSquare, label: 'Chat', path: '/chat' },
        ]
      }
    ];
  }, [role]);

  const getRoleBadge = () => {
    switch (role) {
      case 'va': return { label: 'Virtual Assistant', color: 'bg-blue-50 text-blue-700 border border-blue-200' };
      case 'admin': return { label: 'Administrator', color: 'bg-purple-50 text-purple-700 border border-purple-200' };
      default: return { label: 'Client', color: 'bg-[#E5F7E7] text-[#4BB854] border border-[#5FD068]/20' };
    }
  };

  const roleBadge = getRoleBadge();
  const sidebarWidth = collapsed ? 'w-16' : 'w-60';

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Top Navigation */}
      <header className="bg-white dark:bg-[#1e2035] border-b border-[#E5E7EB] dark:border-[#2e3050] px-3 sm:px-6 py-3 sticky top-0 z-50 overflow-hidden">
        <div className="flex items-center justify-between min-w-0">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden text-[#4B5563] hover:text-[#0A0D0A]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/dashboard" className="flex items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_virtual-admin-1/artifacts/zwdakbm3_New%20Logo%20HD.png"
                alt="Nimble Assistants"
                className="h-8"
              />
            </Link>
            <span className={`hidden sm:inline-flex text-xs px-2.5 py-1 rounded-md font-medium ${roleBadge.color}`} data-testid="user-role-badge">
              {role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
              {roleBadge.label}
            </span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button
              onClick={() => {
                const event = new KeyboardEvent('keydown', { key: 'k', metaKey: true, ctrlKey: true });
                window.dispatchEvent(event);
              }}
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-[#9CA3AF] bg-[#F3F4F6] rounded-lg border border-[#E5E7EB] hover:border-[#5FD068]/40 hover:text-[#4B5563] transition-colors"
              data-testid="cmd-k-trigger"
            >
              <Search className="w-3.5 h-3.5" />
              <span>Search</span>
              <kbd className="ml-1 px-1 py-0.5 text-[10px] bg-white rounded border border-[#E5E7EB] font-mono">
                {navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}K
              </kbd>
            </button>
            <div className="hidden md:block">
              <TimezoneDisplay compact={true} />
            </div>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-[#F3F4F6] text-[#4B5563]"
                  data-testid="settings-gear-btn"
                  title="Settings"
                >
                  <Settings className="w-[18px] h-[18px]" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-my-profile">
                  <UserCog className="w-4 h-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/notification-preferences')} data-testid="menu-notif-prefs">
                  <Bell className="w-4 h-4 mr-2" />
                  Notification Preferences
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-[#EF4444] hover:text-red-600 hover:bg-red-50/50 text-sm"
              data-testid="logout-btn"
            >
              <LogOut className="w-[18px] h-[18px] mr-1.5" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          ref={sidebarRef}
          onScroll={handleSidebarScroll}
          className={`
            fixed top-[57px] left-0 h-[calc(100vh-57px)] bg-white dark:bg-[#161729] border-r border-[#E5E7EB] dark:border-[#2e3050]
            transition-all duration-300 ease-in-out z-40 overflow-y-auto overflow-x-hidden
            ${sidebarWidth}
            ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          {/* Collapse toggle */}
          <div className="hidden lg:flex items-center justify-end px-3 py-2 border-b border-[#E5E7EB]">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-1.5 rounded-md text-[#9CA3AF] hover:text-[#4B5563] hover:bg-[#F3F4F6] transition-colors"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              data-testid="sidebar-collapse-toggle"
            >
              {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
          </div>

          <nav className="py-2 pb-20">
            {sidebarGroups.map((group) => (
              <SidebarGroup
                key={group.title}
                title={group.title}
                items={group.items}
                location={location}
                collapsed={collapsed}
                onNavClick={handleNavClick}
              />
            ))}

            {/* My Profile at bottom */}
            {!collapsed && (
              <div className="mt-4 px-2 pt-4 border-t border-[#E5E7EB]">
                <div className="flex items-center gap-3 px-3 py-2">
                  <div className="w-8 h-8 rounded-full bg-[#E5F7E7] flex items-center justify-center text-[#5FD068] text-xs font-medium">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0A0D0A] truncate">{user.name || 'User'}</p>
                    <p className="text-xs text-[#9CA3AF] truncate">{user.email || ''}</p>
                  </div>
                </div>
              </div>
            )}
          </nav>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 min-w-0 overflow-x-hidden transition-all duration-300 bg-gray-50 dark:bg-[#111318] ${collapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
          <EmailVerificationBanner />
          <div className="p-4 sm:p-6 lg:p-8">
          {children || <Outlet />}
          </div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Command Palette (Cmd+K / Ctrl+K) */}
      <CommandPalette />
    </div>
  );
};

export default DashboardLayout;
